import gsap from "gsap";
import { ReelsMatrix } from "onearm";
import { CascadeAnimation } from "onearm";

/**
 * @description Act for playing looped intro animation with pre-computed steps
 */
export class IntroAnimationAct {
    /**
     * @param {Object} params
     * @param {Object} params.reelsScene - ReelsScene instance
     * @param {Array} params.steps - Pre-computed animation steps
     * @param {Object} params.symbols - Symbols configuration
     */
    constructor({ reelsScene, steps, symbols }) {
        this.reelsScene = reelsScene;
        this.steps = steps;
        this.symbols = symbols;
        this.isPlaying = false;
        this.currentTimeline = null;
        this.symbolsById = {};

        symbols.forEach(symbol => {
            this.symbolsById[symbol.id] = symbol;
        });

        this.cascadeAnimation = new CascadeAnimation(reelsScene);
    }

    /**
     * @description Starts the looped intro animation
     */
    play() {
        this.isPlaying = true;
        this.playStep(0);
    }

    /**
     * @description Plays a single step and chains to the next
     * @param {number} index - Step index
     */
    playStep(index) {
        if (!this.isPlaying) return;

        if (index >= this.steps.length) {
            this.playStep(0);
            return;
        }

        const step = this.steps[index];
        const timeline = this.executeStep(step);

        if (timeline) {
            this.currentTimeline = timeline;
            timeline.eventCallback("onComplete", () => {
                if (this.isPlaying) {
                    this.playStep(index + 1);
                }
            });
        } else {
            this.playStep(index + 1);
        }
    }

    /**
     * @description Executes a single step and returns timeline if async
     * @param {Object} step - Animation step
     * @returns {gsap.core.Timeline|null}
     */
    executeStep(step) {
        const { type } = step;

        switch (type) {
            case "replace":
                return this.executeReplaceStep(step);
            case "stop":
                return this.executeStopStep(step);
            case "spin":
                return this.executeSpinStep(step);
            case "win":
                return this.executeWinStep(step);
            case "destroy":
                return this.executeDestroyStep(step);
            case "cascade":
                return this.executeCascadeStep(step);
            case "multiplier":
                return this.executeMultiplierStep(step);
            default:
                return null;
        }
    }

    /**
     * @description Executes replace symbols step
     * @param {Object} step
     * @returns {gsap.core.Timeline}
     */
    executeReplaceStep(step) {
        const matrix = this.reelsToMatrix(step.reels);
        this.reelsScene.reels.replaceSymbols(matrix);

        const tl = gsap.timeline();
        tl.add(this.delay(0.3));
        return tl;
    }

    /**
     * @description Executes stop reels animation step
     * @param {Object} step
     * @returns {gsap.core.Timeline}
     */
    executeStopStep(step) {
        const matrix = this.reelsToMatrix(step.reels);
        this.reelsScene.reels.reels.forEach(reel => {
            const symbols = [...reel.children];
            symbols.forEach(symbol => reel.removeSymbol(symbol));
        });
        return this.reelsScene.stop({ matrix });
    }

    /**
     * @description Executes spin reels animation step
     * @returns {gsap.core.Timeline}
     */
    executeSpinStep() {
        return this.reelsScene.spin();
    }

    /**
     * @description Executes win animation step
     * @param {Object} step
     * @returns {gsap.core.Timeline}
     */
    executeWinStep(step) {
        return this.reelsScene.getWinAnimationByPositions(step.positions);
    }

    /**
     * @description Executes destroy step - removes symbols without animation
     * @param {Object} step
     * @returns {null}
     */
    executeDestroyStep(step) {
        this.reelsScene.removeSymbolsByPositions(step.positions);
        return null;
    }

    /**
     * @description Executes cascade animation step
     * @param {Object} step
     * @returns {gsap.core.Timeline}
     */
    executeCascadeStep(step) {
        const cascadeData = {
            movements: step.movements,
            newSymbols: this.convertNewSymbols(step.newSymbols),
        };

        const tl = gsap.timeline();
        tl.add(this.cascadeAnimation.build(cascadeData));
        tl.add(this.delay(0.2));
        return tl;
    }

    /**
     * @description Executes multiplier animation step
     * @param {Object} step
     * @returns {gsap.core.Timeline}
     */
    executeMultiplierStep(step) {
        const tl = gsap.timeline();

        step.multipliers.forEach(({ row, column, multiplier }) => {
            const symbol = this.reelsScene.reels.getSymbolByPosition({ row, column });

            tl.add(symbol.playTriggerAnimation());
            tl.to(symbol.multiplier, { pixi: { alpha: 0 }, duration: 0.2}, "-=0.2");
        });

        tl.add(this.delay(0.5));
        return tl;
    }

    /**
     * @description Converts reels array to ReelsMatrix
     * @param {Array} reels - Reels in [column][row] format with symbol data
     * @returns {ReelsMatrix}
     */
    reelsToMatrix(reels) {
        const columns = reels.length;
        const rows = reels[0].length;
        const matrix = new ReelsMatrix({ rows, columns, firstRow: 0, firsColumn: 0 });

        for (let column = 0; column < columns; column++) {
            for (let row = 0; row < rows; row++) {
                const cellData = reels[column][row];
                const symbolId = cellData.id;
                const symbol = { ...this.symbolsById[symbolId] };
                if (cellData.multiplier) {
                    symbol.multiplier = cellData.multiplier;
                }
                matrix.setCell(row, column, symbol);
            }
        }

        return matrix;
    }

    /**
     * @description Converts newSymbols from {symbolId} to {symbolData}
     * @param {Object} newSymbols - New symbols by column
     * @returns {Object}
     */
    convertNewSymbols(newSymbols) {
        const result = {};

        Object.keys(newSymbols).forEach(col => {
            result[col] = newSymbols[col].map(item => {
                const symbolData = { ...this.symbolsById[item.symbolId] };
                if (item.multiplier) {
                    symbolData.multiplier = item.multiplier;
                }
                return {
                    row: item.row,
                    symbolData,
                };
            });
        });

        return result;
    }

    /**
     * @description Creates a delay tween
     * @param {number} time - Delay duration
     * @returns {gsap.core.Tween}
     */
    delay(time) {
        return gsap.delayedCall(time, () => {});
    }

    /**
     * @description Stops the intro animation
     */
    stop() {
        this.isPlaying = false;
        if (this.currentTimeline) {
            this.currentTimeline.kill();
            this.currentTimeline = null;
        }
        this.reelsScene.goToIdle();
    }

    /**
     * @description Destroys the act
     */
    destroy() {
        this.stop();
    }
}
